import { config } from '@iheartjane/config';
import startCase from 'lodash-es/startCase';

import { getFingerprintId, sendAnalytics } from '@jane/shared/data-access';
import {
  Storage,
  TESTS,
  generateFallbackJdid,
  getCookie,
  getLocalStorageWithTTL,
  jdidIsNano,
} from '@jane/shared/util';

import { request } from './lib/request';

export const fpKey = 'j_eyeD';
export const jdidKey = 'jdid';

export const bootstrapUrl = () => `${config.apiPath}/bootstrap`;

// Handle marketplace mode
async function handleMarketplace(documentCookie = document.cookie) {
  if (!Storage.get(jdidKey)) {
    await request(bootstrapUrl(), { method: 'GET' });
    const cookieJdid = getCookie(jdidKey, documentCookie);

    if (cookieJdid) {
      Storage.set(jdidKey, cookieJdid);
      sendAnalytics({ source: 'cookie', jdid: cookieJdid });
      return;
    }

    const fallbackJdid = generateFallbackJdid();
    Storage.set(jdidKey, fallbackJdid);
    sendAnalytics({
      source: 'cookie-fallback',
      jdid: fallbackJdid,
      fallbackId: fallbackJdid,
    });
  }
}

// Handle case when JDID is already available in storage
async function handleExistingJdid(
  jdid: string,
  cachedFingerprintId: string | null
) {
  Storage.set(jdidKey, jdid);
  console.debug('JDID was available in cache');
  sendAnalytics({ source: 'cache', jdid });

  // Try to refresh fingerprint if we have a nanoid JDID
  if (jdidIsNano(jdid) && !cachedFingerprintId) {
    const newJdid = await getFingerprintId(jdid);
    if (newJdid) {
      console.debug('Refreshed cache for FPID');
      sendAnalytics({ source: 'stale-refresh', jdid, fpId: newJdid });
    }
  }
}

// Handle case when JDID is not available in storage
async function handleMissingJdid(cachedFingerprintId: string | null) {
  if (cachedFingerprintId) {
    console.debug('Set JDID to cached FPID');
    Storage.set(jdidKey, cachedFingerprintId);
    sendAnalytics({ source: 'cached-fpid', jdid: cachedFingerprintId });
    return;
  }

  const newJdid = await getFingerprintId();
  if (newJdid) {
    console.debug('Set JDID to new FPID');
    Storage.set(jdidKey, newJdid);
    sendAnalytics({ source: 'new-fpid', jdid: newJdid });
    return;
  }

  const fallbackJdid = generateFallbackJdid();
  console.debug('Set JDID to fallback');
  Storage.set(jdidKey, fallbackJdid);
  sendAnalytics({
    source: 'fallback',
    jdid: fallbackJdid,
    fallbackId: fallbackJdid,
  });
}

interface BootstrapOptions {
  documentCookie?: string;
  isMarketplace?: boolean;
  jdidOverride?: string | null;
}

export async function bootstrap({
  documentCookie = document.cookie,
  isMarketplace = false,
  jdidOverride = null,
}: BootstrapOptions = {}) {
  if (Storage.get('user_session')) {
    // clear out old auth storage
    Storage.remove('user_session');
  }

  if (Storage.get('store-snapshot')) {
    // don't use legacy local cart persistence
    Storage.remove('store-snapshot');
  }

  // Fall back to a 5 second ready window in case a page doesn't mark as ready
  setTimeout(function () {
    window.prerenderReady = true;
  }, 5000);

  window.BUILD_VERSION = config.buildVersion?.substring(0, 7) || '';

  Storage.remove('fpId');

  // Main bootstrap logic
  if (isMarketplace) {
    await handleMarketplace(documentCookie);
  } else if (jdidOverride) {
    Storage.set(jdidKey, jdidOverride);
  } else {
    const availableJdid = Storage.get(jdidKey);
    const cachedFingerprintId = getLocalStorageWithTTL(fpKey);
    const isValidJdid =
      availableJdid &&
      availableJdid !== 'undefined' &&
      availableJdid !== 'null';

    if (isValidJdid) {
      await handleExistingJdid(availableJdid, cachedFingerprintId);
    } else {
      await handleMissingJdid(cachedFingerprintId);
    }
  }

  // If JDID is still not set, set it to a fallback value.
  if (!Storage.get(jdidKey)) {
    const fallbackJdid = generateFallbackJdid();
    Storage.set(jdidKey, fallbackJdid);
    sendAnalytics({
      source: 'final-fallback',
      jdid: fallbackJdid,
      fallbackId: fallbackJdid,
    });
  }

  // Remove inactive AB tests from localStorage and Mixpanel super properties.
  try {
    // Remove historical AB tests that are no longer in local storage but in Cookies.
    const historicalTests = [
      'Test: Relevancy Score Algorithm 1',
      'Test: Relevancy Score Algorithm 2',
      'Test: Relevancy Score Algorithm 3',
      'Test: Relevancy Score Algorithm 4',
    ];
    historicalTests.forEach((t) => janeMixpanel.unregister(t));

    Object.entries(localStorage)
      ?.filter((entry) => entry[0].includes('test:'))
      ?.forEach((entry) => {
        const storageKey = entry[0];
        const testName = storageKey.split(':')[1];

        if (!Object.keys(TESTS).includes(testName)) {
          localStorage.removeItem(storageKey);
          janeMixpanel.unregister(`Test: ${startCase(testName)}`);
        }
      });
  } catch {
    // localStorage not available
  }
}
